import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";

const statusLabels = {
  planning: "Planlegger",
  active: "Aktiv",
  on_hold: "På Vent",
  completed: "Fullført",
  cancelled: "Kansellert",
};

const statusColors = {
  planning: "bg-slate-100 text-slate-700",
  active: "bg-blue-100 text-blue-700",
  on_hold: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const priorityLabels = {
  low: "Lav",
  medium: "Middels",
  high: "Høy",
  urgent: "Haster",
};

export default function ProjectReport({ project, onClose }) {
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: tasks = [] } = useQuery({
    queryKey: ["project-tasks", project.id],
    queryFn: () => base44.entities.Task.filter({ project_id: project.id }, "-created_date"),
  });

  const { data: timeEntries = [] } = useQuery({
    queryKey: ["project-time-entries", project.id],
    queryFn: () => base44.entities.TimeEntry.filter({ project_id: project.id }, "-created_date"),
  });

  const { data: comments = [] } = useQuery({
    queryKey: ["project-comments", project.id],
    queryFn: () => base44.entities.Comment.filter({ 
      related_to_type: "project", 
      related_to_id: project.id 
    }, "-created_date"),
  });

  const { data: phases = [] } = useQuery({
    queryKey: ["phases", project.id],
    queryFn: () => base44.entities.Phase.filter({ project_id: project.id }, "order"),
  });

  // Calculate statistics
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === "completed").length;
  const inProgressTasks = tasks.filter(t => t.status === "in_progress").length;
  const todoTasks = tasks.filter(t => t.status === "todo").length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // Time statistics by user
  const timeByUser = timeEntries.reduce((acc, entry) => {
    const key = entry.user_name || entry.user_email;
    if (!acc[key]) {
      acc[key] = { totalMinutes: 0, billableMinutes: 0 };
    }
    acc[key].totalMinutes += entry.duration_minutes || 0;
    if (entry.billable) {
      acc[key].billableMinutes += entry.duration_minutes || 0;
    }
    return acc;
  }, {});

  const totalTimeMinutes = timeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  const totalHours = (totalTimeMinutes / 60).toFixed(1);

  // Recent comments (last 10)
  const recentComments = comments.slice(0, 10);

  // Recent tasks (last 10)
  const recentTasks = tasks.slice(0, 10);

  const handleGeneratePDF = async () => {
    setIsGenerating(true);
    try {
      const reportElement = document.getElementById("project-report-content");
      
      const canvas = await html2canvas(reportElement, {
        scale: 2,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Prosjektrapport_${project.name}_${format(new Date(), "yyyy-MM-dd")}.pdf`);
      toast.success("PDF-rapport generert");
    } catch (error) {
      console.error(error);
      toast.error("Kunne ikke generere PDF");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8 flex items-start justify-center">
        <div className="bg-white rounded-2xl w-full max-w-4xl">
          <div className="flex items-center justify-between p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-zinc-900" />
              <h2 className="text-xl font-semibold text-slate-900">Prosjektrapport</h2>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleGeneratePDF}
                disabled={isGenerating}
                className="bg-zinc-900 hover:bg-zinc-900"
              >
                <Download className="w-4 h-4 mr-2" />
                {isGenerating ? "Genererer..." : "Last ned PDF"}
              </Button>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          <div id="project-report-content" className="p-8 space-y-8 bg-white">
            {/* Header */}
            <div className="border-b border-slate-200 pb-6">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{project.name}</h1>
              <p className="text-slate-600 mb-4">{project.description}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={statusColors[project.status]}>
                  {statusLabels[project.status]}
                </Badge>
                {project.owner_name && (
                  <span className="text-sm text-slate-500">Eier: {project.owner_name}</span>
                )}
                {project.start_date && (
                  <span className="text-sm text-slate-500">
                    Start: {format(new Date(project.start_date), "dd.MM.yyyy")}
                  </span>
                )}
                {project.end_date && (
                  <span className="text-sm text-slate-500">
                    Slutt: {format(new Date(project.end_date), "dd.MM.yyyy")}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-4">
                Rapport generert: {format(new Date(), "dd.MM.yyyy HH:mm", { locale: nb })}
              </p>
            </div>

            {/* Project Status Summary */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Prosjektstatus</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Totale oppgaver</p>
                  <p className="text-2xl font-bold text-slate-900">{totalTasks}</p>
                </Card>
                <Card className="p-4 border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Fullført</p>
                  <p className="text-2xl font-bold text-emerald-600">{completedTasks}</p>
                </Card>
                <Card className="p-4 border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Pågår</p>
                  <p className="text-2xl font-bold text-blue-600">{inProgressTasks}</p>
                </Card>
                <Card className="p-4 border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Å gjøre</p>
                  <p className="text-2xl font-bold text-slate-600">{todoTasks}</p>
                </Card>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                  <span>Fremgang</span>
                  <span className="font-medium">{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div 
                    className="bg-zinc-900 h-3 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
              {phases.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Faser: {phases.length}</p>
                  <div className="space-y-1">
                    {phases.map(phase => (
                      <div key={phase.id} className="flex items-center gap-2 text-sm">
                        <span className="w-2 h-2 rounded-full bg-zinc-900" />
                        <span className="text-slate-600">{phase.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {phase.status === "completed" ? "Fullført" : phase.status === "in_progress" ? "Pågår" : "Ikke startet"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Time Usage by Team Members */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Tidsbruk per Teammedlem</h3>
              <Card className="p-4 border-slate-200">
                <p className="text-sm text-slate-500 mb-3">
                  Total tid: <span className="font-semibold text-slate-900">{totalHours} timer</span>
                </p>
                {Object.keys(timeByUser).length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Ingen tidsregistreringer ennå</p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(timeByUser).map(([userName, data]) => (
                      <div key={userName} className="flex items-center justify-between pb-3 border-b border-slate-100 last:border-0">
                        <div>
                          <p className="font-medium text-slate-900">{userName}</p>
                          <p className="text-xs text-slate-500">
                            Fakturerbar: {(data.billableMinutes / 60).toFixed(1)}t
                          </p>
                        </div>
                        <p className="text-lg font-semibold text-slate-900">
                          {(data.totalMinutes / 60).toFixed(1)}t
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            {/* Recent Comments */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Nylige Kommentarer</h3>
              {recentComments.length === 0 ? (
                <Card className="p-4 border-slate-200">
                  <p className="text-sm text-slate-500 text-center py-4">Ingen kommentarer ennå</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {recentComments.map((comment) => (
                    <Card key={comment.id} className="p-4 border-slate-200">
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-medium text-slate-900 text-sm">{comment.user_name}</p>
                        <p className="text-xs text-slate-500">
                          {format(new Date(comment.created_date), "dd.MM.yyyy HH:mm")}
                        </p>
                      </div>
                      <p className="text-sm text-slate-600">{comment.content}</p>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Tasks */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Nylige Oppgaver</h3>
              {recentTasks.length === 0 ? (
                <Card className="p-4 border-slate-200">
                  <p className="text-sm text-slate-500 text-center py-4">Ingen oppgaver ennå</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {recentTasks.map((task) => (
                    <Card key={task.id} className="p-3 border-slate-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 text-sm">{task.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="text-xs" variant="outline">
                              {task.status === "completed" ? "Fullført" : task.status === "in_progress" ? "Pågår" : "Å gjøre"}
                            </Badge>
                            <Badge className="text-xs" variant="outline">
                              {priorityLabels[task.priority]}
                            </Badge>
                            {task.assigned_to_name && (
                              <span className="text-xs text-slate-500">→ {task.assigned_to_name}</span>
                            )}
                          </div>
                        </div>
                        {task.due_date && (
                          <p className="text-xs text-slate-500">
                            {format(new Date(task.due_date), "dd.MM.yyyy")}
                          </p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}